import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";

import { cn } from "../lib/cn";

/**
 * Status indicators — "Incubating", "In aviary", "Quarantine".
 *
 * Never a solid brand fill: a tinted ground with full-strength text stays readable at a
 * glance down a long list, where a row of saturated blocks does not. The one exception
 * is `alarm`, solid terracotta, because something late has to stop the eye.
 */
const statusPillVariants = cva(
  cn("inline-flex shrink-0 items-center gap-xs rounded-full uppercase"),
  {
    variants: {
      tone: {
        /** Default, informational: a bird simply in the aviary. */
        neutral: "bg-surface-container-low text-on-surface-body",
        /** Something is going well — fertile eggs, a healthy bird. */
        positive: "bg-primary-fixed text-on-primary-fixed-variant",
        /** A biological phase in progress: laying, incubating, rearing. */
        biological: "bg-tertiary-fixed text-on-tertiary-fixed-variant",
        /** Needs attention soon: a treatment due, a clutch overdue. */
        attention: "bg-secondary-fixed text-on-secondary-fixed-variant",
        /** Something went wrong: a failed round, dead in shell. The deeper terracotta. */
        critical: "bg-secondary-fixed-dim text-on-secondary-fixed",
        /** Solid terracotta, and reserved: something late — "1 late", "Ring now". */
        alarm: "bg-action text-on-action",
      },
      size: {
        md: "h-6 px-2.5 text-tag",
        /**
         * For a pill inside a tile that has to share its row. At 11px the pill's own
         * intrinsic width pushes a two-up card past its half of a 354px screen.
         */
        sm: "h-5.5 px-2.25 text-tag",
        /** The alarm beside a heading — "1 late". */
        lg: "h-6.5 px-2.75 text-tag-lg",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "md",
    },
  },
);

export interface StatusPillProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusPillVariants> {}

export function StatusPill({ className, tone, size, ...props }: StatusPillProps) {
  return <span className={cn(statusPillVariants({ tone, size }), className)} {...props} />;
}

export { statusPillVariants };
